import { ArrowCounterClockwiseIcon } from '@app/components/icons/arrow-counter-clockwise';
import { Copy } from '@app/components/icons/copy';
import { ThumbsDown } from '@app/components/icons/thumbs-down';
import { ThumbsDownOutline } from '@app/components/icons/thumbs-down-outline';
import { ThumbsUp } from '@app/components/icons/thumbs-up';
import { ThumbsUpOutline } from '@app/components/icons/thumbs-up-outline';
import { Tooltip } from '@app/components/tooltip';
import type {
  FeedbackType,
  InlineFeedbackSubmitHandler,
} from '@app/routes/content/routes/recordings/components/content-composer/event';
import {
  cleanHtmlForClipboard,
  extractPlainTextFromHtml,
} from '@app/routes/content/routes/recordings/utils';
import { logger } from '@app/utilities/logger';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-hot-toast';

export interface ContentCardFooterProps {
  contentToCopy: string;
  selectedFeedback?: FeedbackType | null;
  onFeedbackSubmit: InlineFeedbackSubmitHandler;
  onFeedbackChange?: (feedbackType: FeedbackType | null) => void;
  onThumbsDownClick?: () => void;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
  className?: string;
  /** When true, disables the copy button (e.g., when content hasn't loaded yet) */
  isCopyDisabled?: boolean;
  /** Called when content is successfully copied to clipboard */
  onCopySuccess?: () => void;
  /**
   * Footer display mode:
   * - 'full': Shows all buttons (copy, feedback, regenerate)
   * - 'copy-feedback-only': Shows copy and feedback buttons only (used when regenerate is not available)
   * - 'regenerate-only': Shows only the regenerate button (used for failed state)
   */
  mode?: 'full' | 'copy-feedback-only' | 'regenerate-only';
  showFeedback?: boolean;
}

export const ContentCardFooter: React.FC<ContentCardFooterProps> = ({
  contentToCopy,
  selectedFeedback: controlledSelectedFeedback,
  onFeedbackSubmit,
  onFeedbackChange,
  onThumbsDownClick,
  onRegenerate,
  isRegenerating = false,
  className = '',
  isCopyDisabled = false,
  onCopySuccess,
  mode = 'full',
  showFeedback = true,
}) => {
  const { t } = useTranslation();

  // Internal state for feedback (used if not controlled)
  const [internalSelectedFeedback, setInternalSelectedFeedback] =
    useState<FeedbackType | null>(null);
  const [showTextbox, setShowTextbox] = useState(false);
  const [additionalFeedback, setAdditionalFeedback] = useState('');

  // Use controlled value if provided, otherwise use internal state
  const selectedFeedback =
    controlledSelectedFeedback !== undefined
      ? controlledSelectedFeedback
      : internalSelectedFeedback;

  const setSelectedFeedback = useCallback(
    (feedback: FeedbackType | null) => {
      if (onFeedbackChange) {
        onFeedbackChange(feedback);
      } else {
        setInternalSelectedFeedback(feedback);
      }
    },
    [onFeedbackChange],
  );

  // Show textbox when thumbs down is selected
  useEffect(() => {
    if (selectedFeedback === 'thumbs_down') {
      setShowTextbox(true);
    } else {
      setShowTextbox(false);
      setAdditionalFeedback('');
    }
  }, [selectedFeedback]);

  const handleCopy = useCallback(
    async (text: string, result: boolean) => {
      if (result) {
        if (navigator.clipboard && window.ClipboardItem) {
          try {
            const cleanedHtml = cleanHtmlForClipboard(text);
            const plainText = extractPlainTextFromHtml(cleanedHtml);
            const clipboardItem = new ClipboardItem({
              'text/html': new Blob([cleanedHtml], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' }),
            });
            await navigator.clipboard.write([clipboardItem]);
          } catch (error) {
            logger.warning(
              `Could not add plain text format: ${
                error instanceof Error ? error.message : String(error)
              }`,
            );
          }
        }
        toast.success(t('content-recording.content-composer.actions.copied'));
        onCopySuccess?.();
      } else {
        toast.error(
          t('content-recording.content-composer.actions.copy-failed'),
        );
      }
    },
    [t, onCopySuccess],
  );

  const handleThumbsUp = useCallback(() => {
    if (selectedFeedback === 'thumbs_up') return;
    setSelectedFeedback('thumbs_up');
    onFeedbackSubmit('thumbs_up');
    toast.success(
      t('content-recording.content-composer.feedback.thanks-for-feedback'),
    );
  }, [selectedFeedback, onFeedbackSubmit, setSelectedFeedback, t]);

  const handleThumbsDown = useCallback(() => {
    if (selectedFeedback === 'thumbs_down') return;
    setSelectedFeedback('thumbs_down');
    onThumbsDownClick?.();
  }, [selectedFeedback, setSelectedFeedback, onThumbsDownClick]);

  const handleSubmitNegativeFeedback = useCallback(() => {
    onFeedbackSubmit('thumbs_down', additionalFeedback || undefined);
    setShowTextbox(false);
    toast.success(
      t('content-recording.content-composer.feedback.thanks-for-feedback'),
    );
  }, [onFeedbackSubmit, additionalFeedback, t]);

  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setAdditionalFeedback(e.target.value);
    },
    [],
  );

  const handleTextareaKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && e.ctrlKey) {
        handleSubmitNegativeFeedback();
      }
    },
    [handleSubmitNegativeFeedback],
  );

  const isRegenerateOnly = mode === 'regenerate-only';
  const showRegenerateButton = mode !== 'copy-feedback-only' && !!onRegenerate;

  return (
    <div
      className={cx(
        'flex flex-col gap-4 pt-4 border-t border-white/20',
        className,
      )}
      data-testid="content-card-footer"
    >
      {/* First row: Copy button + Thumbs icons (horizontal) */}
      <div className="flex items-center gap-2">
        {/* Copy button - hidden in regenerate-only mode */}
        {!isRegenerateOnly && (
          <Tooltip
            tooltip={t('content-recording.content-composer.actions.copy')}
            placement="top-center"
          >
            <CopyToClipboard
              text={contentToCopy || ''}
              options={{
                format: 'text/html',
              }}
              onCopy={handleCopy}
            >
              <button
                type="button"
                disabled={isCopyDisabled}
                className={cx(
                  'flex items-center justify-center transition-colors duration-200 w-9 h-9',
                  'hover:bg-white/10 rounded',
                  isCopyDisabled && 'opacity-50 cursor-not-allowed',
                )}
                aria-label={t(
                  'content-recording.content-composer.actions.copy',
                )}
                data-testid="content-card-footer-copy"
              >
                <Copy className="size-5 text-white cursor-pointer" />
              </button>
            </CopyToClipboard>
          </Tooltip>
        )}

        {!isRegenerateOnly && showFeedback && (
          <>
            <Tooltip
              tooltip={t(
                'content-recording.content-composer.feedback.thumbs-up-label',
              )}
              placement="top-center"
            >
              <button
                type="button"
                onClick={handleThumbsUp}
                className={cx(
                  'flex items-center justify-center transition-colors duration-200 w-9 h-9',
                  'hover:bg-white/10 rounded',
                )}
                aria-label={t(
                  'content-recording.content-composer.feedback.thumbs-up-label',
                )}
                data-testid="content-card-footer-thumbs-up"
              >
                {selectedFeedback === 'thumbs_up' ? (
                  <ThumbsUp className="size-5 text-white" />
                ) : (
                  <ThumbsUpOutline className="size-5 text-white" />
                )}
              </button>
            </Tooltip>
            <Tooltip
              tooltip={t(
                'content-recording.content-composer.feedback.thumbs-down-label',
              )}
              placement="top-center"
            >
              <button
                type="button"
                onClick={handleThumbsDown}
                className={cx(
                  'flex items-center justify-center transition-colors duration-200 w-9 h-9',
                  'hover:bg-white/10 rounded',
                )}
                aria-label={t(
                  'content-recording.content-composer.feedback.thumbs-down-label',
                )}
                data-testid="content-card-footer-thumbs-down"
              >
                {selectedFeedback === 'thumbs_down' ? (
                  <ThumbsDown className="size-5 text-white" />
                ) : (
                  <ThumbsDownOutline className="size-5 text-white" />
                )}
              </button>
            </Tooltip>
          </>
        )}

        {/* Regenerate button */}
        {showRegenerateButton && (
          <Tooltip
            tooltip={
              isRegenerating
                ? t('content-recording.lab.content-generation-in-progress')
                : t('content-recording.content-composer.actions.regenerate')
            }
            placement="top-center"
          >
            <button
              type="button"
              onClick={onRegenerate}
              disabled={isRegenerating}
              className={cx(
                'flex items-center justify-center transition-colors duration-200 w-9 h-9',
                'hover:bg-white/10 rounded',
              )}
              aria-label={t(
                'content-recording.content-composer.actions.regenerate',
              )}
              data-testid="content-card-footer-regenerate"
            >
              <ArrowCounterClockwiseIcon className="size-5 text-white" />
            </button>
          </Tooltip>
        )}
      </div>

      {!isRegenerateOnly && showFeedback && showTextbox && (
        <div
          className="flex flex-col gap-3 pt-3 border-t border-white/20"
          data-testid="content-card-footer-feedback-textbox"
        >
          {/* Header */}
          <span className="text-white text-[17px] font-medium leading-[25px] tracking-[-0.2px]">
            {t('content-recording.content-composer.feedback.what-went-wrong')}
          </span>

          {/* Textarea */}
          <textarea
            className={cx(
              'w-full min-h-[48px] bg-spring-neutral-base',
              'border border-white/20 rounded-[10px]',
              'px-4 pt-4 pb-[15px]',
              'text-[#f5f6f9] text-sm leading-5',
              'placeholder:text-[#f5f6f9]/60',
              'resize-none',
            )}
            rows={3}
            placeholder={t(
              'content-recording.content-composer.feedback.tell-us-more-placeholder',
            )}
            value={additionalFeedback}
            onChange={handleTextareaChange}
            onKeyDown={handleTextareaKeyDown}
            aria-label={t(
              'content-recording.content-composer.feedback.tell-us-more-placeholder',
            )}
            data-testid="content-card-footer-textarea"
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
          />

          {/* Submit button */}
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={handleSubmitNegativeFeedback}
              className={cx(
                'h-8 min-w-[56px] px-3 rounded-[10px]',
                'bg-[#356afd] hover:bg-[#2d5ae0]',
                'text-white text-sm font-medium leading-5',
                'transition-colors duration-200',
              )}
              data-testid="content-card-footer-submit"
            >
              {t('content-recording.content-composer.feedback.submit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
